import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const venminder = defineConnector({
  identifier: "venminder",
  name: "Venminder",
  tagLine: "Third-party risk management and due diligence",
  keywords: "vendor risk due diligence oversight contracts questionnaires",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: () => "https://rsd\\.venminder\\.com/api/v1/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create a Venminder API user",
      items: [
        {
          kind: "markdown",
          content: `1. Contact your Venminder Relationship Manager or Account Executive to have API access enabled. It is not included with the standard solution.
2. Ask a Venminder administrator with the **API Key Manager** role to [create an API user](https://developers.venminder.com/318218fb2/p/036ae9-getting-started/b/65ab1d) with a name, description, expiration date, and only the API permissions needed for your workflows.
3. Copy the generated **API Key** and **Client Secret**. The secret is shown only once. Do not select **Create Access Token**; this connector obtains short-lived tokens automatically.

The connection test reads the country reference list, not vendor or due-diligence records. It requires enabled API access and does not verify permissions for your workflows. If access is denied, check API enablement and the API user’s permissions with your administrator rather than granting broader permissions just for the test.`,
        },
      ],
    },
    {
      title: "Enter your API credentials",
      items: [
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: () => "https://login.venminder.com/connect/token",
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_basic_no_urlencode",
    scopes: () => ["venminderApi"],
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://rsd.venminder.com/api/v1/Data/Countries",
      method: "GET",
      headers: { Accept: "application/json" },
      expectedStatus: 200,
      expectedBodyContains: ['"abbreviation"', '"country"', '"dialingCode"'],
    }),
  },
  publicEnvVars: () => ({
    VENMINDER_API_URL: "https://rsd.venminder.com/api/v1",
  }),
  usageHint: () =>
    "Use the Venminder v1 API at https://rsd.venminder.com/api/v1. The connector obtains bearer tokens automatically. The country-list connection test does not verify permissions for vendor, due-diligence, or oversight operations.",
});
